import { useState, useEffect } from 'react';
import { hasModuleAccess, getModuleAccessMap } from '@/lib/hasModuleAccess';

/**
 * React hook — single module access check.
 * Returns { checking: true } while resolving, then { checking: false, hasAccess: bool }.
 *
 * @example
 *   const { checking, hasAccess } = useModuleAccess('whatsapp_notifications');
 *   if (checking) return <Spinner />;
 *   if (!hasAccess) return <Locked />;
 */
export function useModuleAccess(moduleId: string) {
  const [checking, setChecking]   = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    hasModuleAccess(moduleId).then((access) => {
      if (!cancelled) {
        setHasAccess(access);
        setChecking(false);
      }
    });
    return () => { cancelled = true; };
  }, [moduleId]);

  return { checking, hasAccess };
}

/**
 * React hook — check multiple modules at once.
 * Returns { checking: true } while resolving, then a map of { moduleId: boolean }.
 *
 * @example
 *   const { checking, access } = useModuleAccessMap(['digital_invitations', 'ai_sales_assistant']);
 *   if (access.digital_invitations) { ... }
 */
export function useModuleAccessMap(moduleIds: string[]) {
  const [checking, setChecking] = useState(true);
  const [access, setAccess]     = useState<Record<string, boolean>>({});

  // Stable serialized key so effect doesn't re-run on array identity changes
  const key = moduleIds.slice().sort().join(',');

  useEffect(() => {
    if (!key) { setChecking(false); return; }
    let cancelled = false;
    getModuleAccessMap(moduleIds).then((map) => {
      if (!cancelled) {
        setAccess(map);
        setChecking(false);
      }
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { checking, access };
}
